import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Paper,
  Chip,
  Alert,
  LinearProgress,
  IconButton,
  Collapse
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  SkipNext as NextIcon,
  SkipPrevious as PrevIcon,
  CheckCircle as CompleteIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  Lightbulb as TipIcon
} from '@mui/icons-material';
import Screenshot from './Screenshot';

const Tutorial = ({ 
  title, 
  description, 
  estimatedTime,
  difficulty = 'Beginner',
  steps = [],
  onComplete
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [completed, setCompleted] = useState(new Set());
  const [isPlaying, setIsPlaying] = useState(false);
  const [showTips, setShowTips] = useState(true);

  const handleNext = () => {
    if (activeStep < steps.length - 1) {
      setActiveStep(activeStep + 1);
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
    }
  };

  const handleStepClick = (stepIndex) => {
    setActiveStep(stepIndex);
  };

  const handleStepComplete = (stepIndex) => {
    const newCompleted = new Set(completed);
    newCompleted.add(stepIndex);
    setCompleted(newCompleted);
    
    if (newCompleted.size === steps.length && onComplete) {
      onComplete();
    }
  };

  const handleReset = () => {
    setActiveStep(0);
    setCompleted(new Set());
    setIsPlaying(false);
  };

  const progress = (completed.size / steps.length) * 100;
  const currentStep = steps[activeStep];

  const getDifficultyColor = (level) => {
    switch (level.toLowerCase()) {
      case 'beginner': return 'success';
      case 'intermediate': return 'warning';
      case 'advanced': return 'error';
      default: return 'info';
    }
  };

  return (
    <Box sx={{ mb: 4 }}>
      {/* Tutorial Header */}
      <Card sx={{ mb: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <PlayIcon sx={{ mr: 1, fontSize: 28 }} />
            <Typography variant="h4" sx={{ fontWeight: 600, flexGrow: 1 }}>
              {title}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Chip 
                label={difficulty} 
                color={getDifficultyColor(difficulty)}
                variant="filled"
                sx={{ color: 'white' }}
              />
              <Chip 
                label={`${estimatedTime} min`} 
                variant="outlined"
                sx={{ color: 'white', borderColor: 'white' }}
              />
            </Box>
          </Box>
          <Typography variant="body1" sx={{ mb: 3, opacity: 0.9 }}>
            {description}
          </Typography>
          
          {/* Progress Bar */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2">Progress</Typography>
              <Typography variant="body2">{completed.size}/{steps.length} steps completed</Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={progress} 
              sx={{ 
                height: 8, 
                borderRadius: 4,
                backgroundColor: 'rgba(255,255,255,0.3)',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: 'white'
                }
              }} 
            />
          </Box>

          {/* Tutorial Controls */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Button
              variant="contained"
              color="secondary"
              startIcon={isPlaying ? <PauseIcon /> : <PlayIcon />}
              onClick={() => setIsPlaying(!isPlaying)}
              sx={{ backgroundColor: 'white', color: 'primary.main' }}
            >
              {isPlaying ? 'Pause Tutorial' : 'Start Tutorial'}
            </Button>
            <Button
              variant="outlined"
              onClick={handleReset}
              sx={{ color: 'white', borderColor: 'white' }}
            >
              Reset
            </Button>
            <IconButton
              onClick={() => setShowTips(!showTips)}
              sx={{ color: 'white' }}
            >
              <TipIcon />
            </IconButton>
          </Box>
        </CardContent>
      </Card>

      {/* Tips Section */}
      <Collapse in={showTips}>
        <Alert 
          severity="info" 
          sx={{ mb: 3 }}
          action={
            <IconButton
              color="inherit"
              size="small"
              onClick={() => setShowTips(false)}
            >
              <CollapseIcon />
            </IconButton>
          }
        >
          <Typography variant="subtitle2" gutterBottom>💡 Tutorial Tips</Typography>
          <Typography variant="body2">
            • Click on any step to jump to it directly<br/>
            • Mark steps as complete using the checkboxes<br/>
            • Screenshots are clickable for full-size view<br/>
            • Take your time - this tutorial is self-paced
          </Typography>
        </Alert>
      </Collapse>

      {/* Tutorial Steps */}
      <Card>
        <CardContent sx={{ p: 0 }}>
          <Stepper activeStep={activeStep} orientation="vertical">
            {steps.map((step, index) => (
              <Step key={index} completed={completed.has(index)}>
                <StepLabel
                  onClick={() => handleStepClick(index)}
                  sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 500 }}>
                      {step.title}
                    </Typography>
                    {step.duration && (
                      <Chip label={`${step.duration} min`} size="small" variant="outlined" />
                    )}
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {step.description}
                  </Typography>
                </StepLabel>
                <StepContent>
                  <Paper sx={{ p: 3, mt: 2, backgroundColor: 'grey.50' }}>
                    {/* Step Content */}
                    <Box sx={{ mb: 3 }}>
                      {step.content}
                    </Box>

                    {/* Step Screenshot */}
                    {step.screenshot && (
                      <Box sx={{ mb: 3 }}>
                        <Screenshot
                          src={step.screenshot.src}
                          alt={step.screenshot.alt}
                          caption={step.screenshot.caption}
                        />
                      </Box>
                    )}

                    {/* Step Actions */}
                    {step.actions && step.actions.length > 0 && (
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 500 }}>
                          Action Items:
                        </Typography>
                        <Box sx={{ pl: 2 }}>
                          {step.actions.map((action, actionIndex) => (
                            <Typography key={actionIndex} variant="body2" sx={{ mb: 1 }}>
                              {actionIndex + 1}. {action}
                            </Typography>
                          ))}
                        </Box>
                      </Box>
                    )}

                    {/* Step Tips */}
                    {step.tips && step.tips.length > 0 && (
                      <Alert severity="success" sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" gutterBottom>💡 Pro Tips</Typography>
                        {step.tips.map((tip, tipIndex) => (
                          <Typography key={tipIndex} variant="body2" sx={{ mb: 0.5 }}>
                            • {tip}
                          </Typography>
                        ))}
                      </Alert>
                    )}

                    {/* Navigation Buttons */}
                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          disabled={activeStep === 0}
                          onClick={handleBack}
                          startIcon={<PrevIcon />}
                          variant="outlined"
                        >
                          Previous
                        </Button>
                        <Button
                          onClick={handleNext}
                          disabled={activeStep === steps.length - 1}
                          endIcon={<NextIcon />}
                          variant="contained"
                        >
                          {activeStep === steps.length - 1 ? 'Finish' : 'Next Step'}
                        </Button>
                      </Box>
                      
                      <Button
                        variant={completed.has(index) ? "contained" : "outlined"}
                        color={completed.has(index) ? "success" : "primary"}
                        startIcon={completed.has(index) ? <CompleteIcon /> : null}
                        onClick={() => handleStepComplete(index)}
                        size="small"
                      >
                        {completed.has(index) ? 'Completed' : 'Mark Complete'}
                      </Button>
                    </Box>
                  </Paper>
                </StepContent>
              </Step>
            ))}
          </Stepper>
        </CardContent>
      </Card>

      {/* Completion Message */}
      {completed.size === steps.length && (
        <Alert severity="success" sx={{ mt: 3 }}>
          <Typography variant="h6" gutterBottom>🎉 Tutorial Completed!</Typography>
          <Typography variant="body2">
            Congratulations! You've completed the "{title}" tutorial. 
            You're now ready to use this feature effectively.
          </Typography>
        </Alert>
      )}
    </Box>
  );
};

export default Tutorial;